import * as THREE from 'three/webgpu';
import { mint } from '@madar/shared';

/**
 * THE MODEL'S MATERIALS — real ones, not the interface's palette.
 *
 * The first passes drew مطل in the brand's graphite and mint, and Ghassan's
 * verdict was "gray looking boxes … I want this to look like a real model"
 * (2026-10-21), pointing at the Architectural X-ray demo in
 * MSM/Interactive 3D house: limestone, oak, glass with interiors behind it,
 * a misty sky, lawn and trees. So the building is drawn in its materials,
 * read off the photos in MSM/Matal (warm stone, charcoal frames, wood,
 * planted terraces), and the brand stays where it belongs: the interface,
 * and the one signal the model carries for it — mint means available.
 */
export const MAT = {
  stone: '#d8cdbb',
  stoneJoint: '#b9ad99',
  concrete: '#9b9892',
  slab: '#e4ded3',
  charcoal: '#2a2b2e',
  oak: '#a5764b',
  oakDark: '#7d5534',
  glass: '#9db4be',
  planter: '#bcb09e',
  leaves: ['#4b6a38', '#5d7c44', '#3d5831', '#6b8a4c'],
  trunk: '#6e5a45',
  canopy: '#f1ece2',
  tableTop: '#b38858',
  chair: '#3b322b',
  timberFloor: '#9a7552',
  emptyFloor: '#cfc8bc',
  warmLight: '#ffd2a1',
  water: '#245f73',
  paving: '#ddd3c2',
  pavingJoint: '#c5b9a5',
  lawn: '#7a9358',
  towerGlass: '#5d7f92',
  towerBand: '#dedbd4',
  available: mint[400],
  availableGlow: mint[300],
  xray: mint[300],
} as const;

/** Day and dusk skies, top to horizon, as the demo grades them. */
export const SKY = {
  day: { top: '#8fa3b3', horizon: '#dfe4e7', fog: '#dde2e5' },
  // Blue hour, not a purple murk: deep navy overhead, a lighter band at the horizon.
  dusk: { top: '#02061a', horizon: '#1d2748', fog: '#111a33' },
  xray: { top: '#0a0f14', horizon: '#1c262d', fog: '#141c22' },
} as const;

/** A vertical gradient the renderer draws as the backdrop. */
export function skyTexture(stops: string[]) {
  const canvas = document.createElement('canvas');
  canvas.width = 4;
  canvas.height = 512;
  const g = canvas.getContext('2d')!;
  const gradient = g.createLinearGradient(0, 0, 0, 512);
  stops.forEach((c, i) => gradient.addColorStop(i / (stops.length - 1), c));
  g.fillStyle = gradient;
  g.fillRect(0, 0, 4, 512);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

/**
 * A tiling ripple normal map for the water, drawn from a few crossed sine
 * waves so there is nothing to download.
 */
export function waterNormals() {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const g = canvas.getContext('2d')!;
  const image = g.createImageData(size, size);
  const h = (x: number, y: number) => {
    const u = (x / size) * Math.PI * 2;
    const v = (y / size) * Math.PI * 2;
    return (
      Math.sin(u * 3 + v * 2) * 0.5 +
      Math.sin(u * 7 - v * 5) * 0.25 +
      Math.sin(u * 13 + v * 11) * 0.12 +
      Math.sin(-u * 5 + v * 17) * 0.08
    );
  };
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = h(x + 1, y) - h(x - 1, y);
      const dy = h(x, y + 1) - h(x, y - 1);
      const n = new THREE.Vector3(-dx * 2.2, -dy * 2.2, 1).normalize();
      const i = (y * size + x) * 4;
      image.data[i] = (n.x * 0.5 + 0.5) * 255;
      image.data[i + 1] = (n.y * 0.5 + 0.5) * 255;
      image.data[i + 2] = (n.z * 0.5 + 0.5) * 255;
      image.data[i + 3] = 255;
    }
  }
  g.putImageData(image, 0, 0);
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  return texture;
}

/** Oak decking, 0.15 m boards, for the terraces. */
export function deckTexture() {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 256;
  const g = canvas.getContext('2d')!;
  for (let i = 0; i < 8; i++) {
    const shade = 0.88 + Math.random() * 0.16;
    const c = new THREE.Color(MAT.oak).multiplyScalar(shade);
    g.fillStyle = `#${c.getHexString()}`;
    g.fillRect(0, i * 32, 256, 32);
    g.fillStyle = 'rgba(40,25,10,0.35)';
    g.fillRect(0, i * 32 + 30, 256, 2);
    for (let k = 0; k < 40; k++) {
      g.fillStyle = `rgba(60,35,15,${Math.random() * 0.08})`;
      g.fillRect(Math.random() * 256, i * 32 + Math.random() * 30, 30 + Math.random() * 60, 1);
    }
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(1 / 1.2, 1 / 1.2);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 8;
  return texture;
}
